import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './FAQ.css';

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = useMemo(() => [
    {
      question: 'How does the process work?',
      answer: [
        'We begin with a strategy consultation to understand your business, goals, and required functionality.',
        "You'll then receive a clear proposal outlining scope, timeline, and investment.",
        'Once approved and the deposit is received, development begins.'
      ]
    },
    {
      question: 'How long does a project take?',
      answer: [
        'Typical timelines:',
        '• Essential Builds: 2–4 weeks',
        '• Growth Platforms: 4–6 weeks',
        '• Bespoke Systems: Project dependent',
        '',
        'Timelines vary based on complexity and feedback turnaround.'
      ]
    },
    {
      question: 'Do you use templates or page builders?',
      answer: [
        'No.',
        'All websites are designed and built specifically for your business. We do not resell themes or deploy generic template sites.'
      ]
    },
    {
      question: 'What is included in the monthly fee?',
      answer: [
        'The monthly fee covers:',
        '• Managed hosting',
        '• Security monitoring',
        '• Performance optimisation',
        '• Automated backups',
        '• Ongoing system oversight',
        '',
        'This ensures your website remains secure, stable, and operational.',
        'New features or structural changes are scoped separately.'
      ]
    },
    {
      question: 'What if I need changes after launch?',
      answer: [
        'Any work outside the original project scope is quoted and approved before development begins.',
        'Additional work is billed at £65 per hour.',
        'This keeps projects structured and transparent.'
      ]
    },
    {
      question: "What if I'm not very technical?",
      answer: [
        "You don't need to be.",
        'We handle the technical setup, hosting, security, and system management. Your focus stays on running your business — not learning software.',
        'Where admin access is required, we provide simple guidance and walkthrough support.'
      ]
    },
    {
      question: 'How do I know this will actually benefit my business?',
      answer: [
        "Every build is structured around your business goals — whether that's generating enquiries, improving client flow, or streamlining operations.",
        "We don't design for appearance alone. We build with function and long-term usability in mind.",
        'During consultation, we define clear objectives so the system supports measurable outcomes.'
      ]
    },
    {
      question: 'Am I locked into a long-term contract?',
      answer: [
        'No long-term contracts are required for standard builds.',
        'The monthly management structure ensures your website remains secure and operational. If you choose to move elsewhere in the future, a structured handover can be arranged.'
      ]
    },
    {
      question: 'Who do you typically work with?',
      answer: [
        'We work with small and growing businesses that want more than a basic website.',
        'From trades and clinics to consultants and service providers, our clients are focused on building a professional online presence and using technology to support long-term growth.',
        'We help businesses move beyond simple templates and towards structured, scalable digital systems.'
      ]
    },
    {
      question: 'Do I own my website?',
      answer: [
        'Yes.',
        'Once the initial build is paid in full, you retain rights to your website assets and domain.',
        'Leviathan manages hosting and technical oversight while your monthly agreement is active.'
      ]
    },
    {
      question: 'Will my website be secure and GDPR compliant?',
      answer: [
        'We implement secure hosting, SSL encryption, and best-practice data handling for forms and integrations.',
        'Legal compliance ultimately remains the responsibility of the business owner, but we build with security and data protection in mind.'
      ]
    },
    {
      question: 'Do you offer payment plans?',
      answer: [
        'Most projects are structured as 50% upfront and 50% upon completion.',
        'Larger builds may be divided into milestone-based payments.'
      ]
    },
    {
      question: 'Do you provide SEO or marketing?',
      answer: [
        'Every website we build includes foundational SEO configuration, structured content hierarchy, performance optimisation, and metadata setup to support search visibility.',
        '',
        'For businesses looking to actively grow through organic traffic, we offer ongoing optimisation and content strategy guidance following launch.',
        '',
        'We focus on sustainable, structured growth — not quick-fix ranking tactics.'
      ]
    }
  ], []);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  useEffect(() => {
    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer.filter(Boolean).join(' ')
        }
      }))
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-schema', 'faq');
    script.text = JSON.stringify(faqSchema);
    document.head.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [faqs]);

  return (
    <section className="faq-section">
      <div className="faq-container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="section-badge">
            <span className="badge-icon">❓</span>
            FREQUENTLY ASKED QUESTIONS
          </div>
          <h2 className="section-title">Questions & Answers</h2>
          <p className="section-description">
            Common questions about our process, pricing, and how we work with businesses.
          </p>
        </motion.div>

        <motion.div
          className="faq-list"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              className={`faq-item ${openIndex === index ? 'active' : ''}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
            >
              <button
                className="faq-question"
                onClick={() => toggleFAQ(index)}
                aria-expanded={openIndex === index}
              >
                <span className="question-text">{faq.question}</span>
                <motion.span
                  className="faq-icon"
                  animate={{ rotate: openIndex === index ? 45 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  +
                </motion.span>
              </button>
              
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    className="faq-answer"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                  >
                    <div className="answer-content">
                      {faq.answer.map((paragraph, i) => (
                        <p key={i}>{paragraph}</p>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          className="faq-cta"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <h3>Still have questions?</h3>
          <p>We're here to help. Get in touch and we'll discuss your project in detail.</p>
          <button
            className="btn-faq-contact"
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          >
            Contact Us
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ;
